import {observer} from 'mobx-react-lite'
import React, {ForwardedRef, useCallback} from 'react'
import {DropArea} from '@common/drag/drag-common'
import {updateRef} from '@containers/element-utils'
import {ScrollDirection, useDnDStore} from './dnd-store'
import {DnDDataObject, DnDDataPayload, DnDDataType} from './types'

export interface DraggableProps<T extends DnDDataType = DnDDataType> {
  children?: JSX.Element | JSX.Element[]
  clickablesOnHover?: boolean
  data: Extract<DnDDataObject, {type: T}>
  disabled?: boolean
  dropArea?: DropArea
  onDragStart?: (dragged: DnDDataPayload<T>) => void
  onDragEnd?: (dragged: DnDDataPayload<T>) => void
  ghost?: HTMLElement | ((ghost: HTMLElement) => HTMLElement)
  placeholder?: HTMLElement | ((rect: DOMRect) => HTMLElement)
  scrollContainers?: {
    selector: string
    direction: ScrollDirection
    /**
     * - If `false` (default), searches the closest parent to the draggable matching the selector.
     * - If `true`, searches `document` children matching the selector.
     */
    absolute?: boolean
    /**
     * Default `false`. If `true` allows drag scrolling when a pointer is outside of container boundaries.
     * Caveat: works only if a single element matches the selector in `absolute` case.
     */
    allowOutside?: boolean
    /**
     * Scroll thresholds:
     * - a single number is applied to both X and Y thresholds
     * - a number tuple applies the first number to X threshold and the second one to Y
     * - a function receives container width&height and returns any option above
     */
    thresholds?:
      | number
      | [number, number]
      | ((width: number, height: number) => number | [number, number])
  }[]
  onMouseDown?: React.MouseEventHandler
  trigger: 'middleX' | 'middleY'
  // Allows movement of a placeholder when a pointer is outside the container element
  allowPlaceholderOutsideMovement?: boolean
  className?: string
  canBeHiddenOnDrag?: boolean
}

export const Draggable = observer(
  React.forwardRef(
    <T extends DnDDataType>(
      {disabled, children, onMouseDown, className, ...props}: DraggableProps<T>,
      ref: ForwardedRef<HTMLDivElement>
    ) => {
      const [, store] = useDnDStore()

      return (
        <div
          ref={ref}
          className={className}
          onMouseDown={e => {
            onMouseDown?.(e)
            !disabled && store.onMouseDown(e, props)
          }}
        >
          {children}
        </div>
      )
    }
  )
)

export type WithDraggableProps<
  T extends DnDDataType,
  P extends {onMouseDown?: React.MouseEventHandler<HTMLElement>}
> = {
  children: React.FunctionComponentElement<P> | false | null | undefined
} & Omit<DraggableProps<T>, 'children' | 'className' | 'style'>

export const WithDraggable = observer(
  React.forwardRef(
    <
      T extends DnDDataType,
      E extends HTMLElement,
      P extends {
        ref?: React.Ref<E>
        onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void
      }
    >(
      {children, ...props}: WithDraggableProps<T, P>,
      forwardedRef: ForwardedRef<HTMLElement>
    ) => {
      const [, store] = useDnDStore()

      // `mousedown` handler should be passed as a React prop and not applied directly
      // to a component ref, b/c otherwise it messes the order of event handlers,
      // and native DnD handlers (e.g. on task card) are executed after custom DnD starts
      const originalCb = children ? children.props.onMouseDown : undefined
      const onMouseDown = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
          originalCb?.(e)
          e.stopPropagation()
          // fix cursor problem in Safari
          e.preventDefault()
          if (!props.disabled) {
            store.onMouseDown(e, props)
          }
        },
        [store, originalCb, props.disabled]
      )

      return children
        ? React.cloneElement(children, {
            ref: (el: HTMLElement) => {
              updateRef(el, forwardedRef)
              updateRef(el, children.ref)
            },
            onMouseDown
          } as any)
        : null
    }
  )
)
